import { z } from 'zod';
import { cents, entityBase, id, organizationId, timestamp } from './common.js';
import { profession } from './professional.js';

/** Whether a shift is filled by professionals requesting (office confirms) or instantly accepting. */
export const bookingMode = z.enum(['request', 'accept']);
export type BookingMode = z.infer<typeof bookingMode>;

export const shiftStatus = z.enum([
  'draft',
  'open',
  'pending_confirmation',
  'filled',
  'in_progress',
  'completed',
  'cancelled',
]);
export type ShiftStatus = z.infer<typeof shiftStatus>;

/** Office list grouping; evaluated server-side so page totals match the selected tab. */
export const officeShiftListView = z.enum(['active', 'history']);
export type OfficeShiftListView = z.infer<typeof officeShiftListView>;

export const shift = entityBase.extend({
  groupId: id,
  locationId: id,
  createdByUserId: id,
  roleRequired: profession,
  startsAt: timestamp,
  endsAt: timestamp,
  breakMinutes: z.number().int().nonnegative(),
  /** Agreed professional hourly rate (cents); required before a new shift can be booked. */
  payRateCents: cents.nullable(),
  bookingMode,
  status: shiftStatus,
  notes: z.string().nullable(),
  cancellationReason: z.string().nullable(),
  cancelledAt: timestamp.nullable(),
  cancelledByUserId: id.nullable(),
  /** If this shift was reposted from a cancelled/unfilled one, its id (repost lineage). */
  repostOfShiftId: id.nullable(),
});
export type Shift = z.infer<typeof shift>;

export const createShiftInput = z
  .object({
    organizationId,
    groupId: id,
    locationId: id,
    createdByUserId: id,
    roleRequired: profession,
    startsAt: timestamp,
    endsAt: timestamp,
    breakMinutes: z.number().int().nonnegative().default(0),
    payRateCents: cents.positive(),
    bookingMode: bookingMode.default('request'),
    notes: z.string().nullish(),
    repostOfShiftId: id.nullish(),
  })
  .refine((s) => s.endsAt > s.startsAt, {
    message: 'endsAt must be after startsAt',
    path: ['endsAt'],
  });
export type CreateShiftInput = z.infer<typeof createShiftInput>;

/** Editable fields when an office amends an existing (not-yet-cancelled/filled) shift. */
export const updateShiftInput = z
  .object({
    locationId: id.optional(),
    roleRequired: profession.optional(),
    startsAt: timestamp.optional(),
    endsAt: timestamp.optional(),
    breakMinutes: z.number().int().nonnegative().optional(),
    payRateCents: cents.positive().optional(),
    bookingMode: bookingMode.optional(),
    notes: z.string().optional(),
  })
  // When both are supplied, enforce order. Partial edits (only one supplied) are re-checked against
  // the existing shift server-side, since the schema can't see the stored value.
  .refine((s) => !(s.startsAt && s.endsAt) || s.endsAt > s.startsAt, {
    message: 'endsAt must be after startsAt',
    path: ['endsAt'],
  });
export type UpdateShiftInput = z.infer<typeof updateShiftInput>;

/** Reason attached when an office cancels a shift. */
export const cancelShiftInput = z.object({ reason: z.string().nullish() });
export type CancelShiftInput = z.infer<typeof cancelShiftInput>;

/**
 * A professional's booking on a shift — unifies the request/accept application AND the resulting
 * assignment in one record. `kind` records how it started; `status` tracks the lifecycle.
 */
export const bookingKind = z.enum(['request', 'accept', 'proposal']);
export type BookingKind = z.infer<typeof bookingKind>;

export const bookingStatus = z.enum([
  'pending',
  'confirmed',
  'declined',
  'withdrawn',
  'cancelled',
  'completed',
  'no_show',
]);
export type BookingStatus = z.infer<typeof bookingStatus>;

/** Professional booking grouping; evaluated server-side before pagination. */
export const bookingListView = z.enum(['upcoming', 'history']);
export type BookingListView = z.infer<typeof bookingListView>;

export const shiftBooking = entityBase.extend({
  shiftId: id,
  professionalId: id,
  agreedPayRateCents: cents.nullable(),
  rateRevision: z.number().int().nonnegative(),
  appliedRateChangeId: id.nullable(),
  agreedOfficeFeeBasisPoints: z.number().int().min(0).max(10000),
  lastTermsEventId: id.nullable(),
  kind: bookingKind,
  status: bookingStatus,
  appliedAt: timestamp,
  decidedByUserId: id.nullable(),
  decidedAt: timestamp.nullable(),
  declineReason: z.string().nullable(),
});
export type ShiftBooking = z.infer<typeof shiftBooking>;

export const createShiftBookingInput = z.object({
  organizationId,
  shiftId: id,
  professionalId: id,
  kind: bookingKind,
});
export type CreateShiftBookingInput = z.infer<typeof createShiftBookingInput>;

/** Immutable recorded check-in / check-out evidence, separate from approved actual hours. */
export const checkEventKind = z.enum(['check_in', 'check_out']);
export type CheckEventKind = z.infer<typeof checkEventKind>;

export const shiftCheckEvent = z.object({
  id,
  organizationId,
  shiftBookingId: id,
  kind: checkEventKind,
  eventAt: timestamp,
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  source: z.enum(['web', 'manual', 'admin']),
  actorUserId: z.string().nullable(),
  createdAt: timestamp,
});
export type ShiftCheckEvent = z.infer<typeof shiftCheckEvent>;
